// SettingsEmployeeList - MODULE
// ***********************************************************************************************************************
import * as React from "react";
import { t } from "i18next";
import * as store from "../../framework/customStore";
import * as baseService from "../../services/baseService";
import * as employeeService from "../../services/employeeService";
import { ToolButton } from "../framework/toolButton";
import { Base } from "../../framework/base";
import { ListSearchFilter } from "../framework/listSearchFilter";
import { ListHeaderColumn } from "../framework/listHeaderColumn";
import { ToolTitle } from "../framework/toolTitle";
import * as StoreActions from "../../models/store/storeActions";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import { IEmployeeItem } from "../../models/employee/employeeItem";
import { IEmployeeEdit } from "../../models/employee/employeeEdit";
import { SettingsEmployeeDialog } from "./settingsEmployeeDialog";
import { infiniteListPageSize } from "../../models/common/consts";
import { handleApiError } from "../../models/store/storeEffects";
import { CheckBox } from "../framework/checkbox";
import { EmployeeOperations } from "../../models/employee/employeeOperations";
import { AvatarImage } from "../framework/avatarImage";
import { UserParameters } from "../../models/employee/userParameters";
import { IEmployeeParameters } from "../../models/employee/employeeIParameters";

// SettingsEmployeeListLineHeader
export interface ISettingsEmployeeListLineHeaderProp {
    sortColumn: string;
    sortOrderIsAsc: boolean;
    onColumnClick: (column: string) => void;
}

export class SettingsEmployeeListLineHeader extends React.Component<ISettingsEmployeeListLineHeaderProp, {}> {
    handleColumnClick = (column: string) => {
        this.props.onColumnClick(column);
    };

    render() {
        return (
            <div className="row title">
                <ListHeaderColumn
                    title="#"
                    column="number"
                    classes="col-1"
                    sortColumn={this.props.sortColumn}
                    sortOrderIsAsc={this.props.sortOrderIsAsc}
                    onClick={this.handleColumnClick}
                />
                <ListHeaderColumn
                    title={t("common.name")}
                    column="name"
                    classes="col-2"
                    sortColumn={this.props.sortColumn}
                    sortOrderIsAsc={this.props.sortOrderIsAsc}
                    onClick={this.handleColumnClick}
                />
                <ListHeaderColumn
                    title={t("common.username")}
                    column="username"
                    classes="col-2"
                    sortColumn={this.props.sortColumn}
                    sortOrderIsAsc={this.props.sortOrderIsAsc}
                    onClick={this.handleColumnClick}
                />
                <ListHeaderColumn
                    title={t("common.userGroup")}
                    column="userGroupName"
                    classes="col-1"
                    sortColumn={this.props.sortColumn}
                    sortOrderIsAsc={this.props.sortOrderIsAsc}
                    onClick={this.handleColumnClick}
                />
                <ListHeaderColumn
                    title={t("employee.employeeGroup")}
                    column="employeeGroupName"
                    classes="col-2"
                    sortColumn={this.props.sortColumn}
                    sortOrderIsAsc={this.props.sortOrderIsAsc}
                    onClick={this.handleColumnClick}
                />
                <ListHeaderColumn
                    title={t("common.phone")}
                    column="phone"
                    classes="col-2"
                    sortColumn={this.props.sortColumn}
                    sortOrderIsAsc={this.props.sortOrderIsAsc}
                    onClick={this.handleColumnClick}
                />
                <ListHeaderColumn
                    title={t("common.registerNumber")}
                    column="vehicleRegisterNumber"
                    classes="col-1"
                    sortColumn={this.props.sortColumn}
                    sortOrderIsAsc={this.props.sortOrderIsAsc}
                    onClick={this.handleColumnClick}
                />
                <ListHeaderColumn
                    title={t("common.inUse")}
                    column="activeState"
                    classes="col-1"
                    sortColumn={this.props.sortColumn}
                    sortOrderIsAsc={this.props.sortOrderIsAsc}
                    onClick={this.handleColumnClick}
                />
            </div>
        );
    }
}

// SettingsEmployeeListLine
export interface ISettingsEmployeeListLineProp {
    item: IEmployeeItem;
    selectedId: string;
    onClick: (id: string) => void;
    onDoubleClick: (id: string) => void;
}

export class SettingsEmployeeListLine extends React.Component<ISettingsEmployeeListLineProp, {}> {
    handleNumberClick = (e) => {
        this.props.onDoubleClick(this.props.item.id);
    };

    render() {
        const item = this.props.item;
        return (
            <div className={"row line" + (item.id === this.props.selectedId ? " selected" : "") + (item.activeState > 0 ? "" : " disabled")} onClick={() => { this.props.onClick(item.id); }} onDoubleClick={() => { this.props.onDoubleClick(item.id); }}>
                <div className="col-1 number ca" onClick={this.handleNumberClick}>{item.number.toString(10)}</div>
                <div className="col-2 withAvatar">
                    <AvatarImage
                        classes="left"
                        image={item.picture}
                        fullText={!!item.abbreviation}
                        text={item.getAvatarText()}
                        name={item.name}
                        showName={true}
                        tooltip={item.name}
                    />
                </div>
                <div className="col-2">{item.username}</div>
                <div className="col-1">{item.userGroupName}</div>
                <div className="col-2">{item.employeeGroupName}</div>
                <div className="col-2">{item.phone}</div>
                <div className="col-1">{item.vehicleRegisterNumber}</div>
                <div className="col-1 inUse ca">{item.activeState > 0 ? <div className="icon check" /> : ""}</div>
            </div>
        );
    }
}

// SettingsEmployeeList
export interface ISettingsEmployeeListProp {
    setEmployeeParameters: (parameters: IEmployeeParameters, saveToDb: boolean) => void;
}

interface ISettingsEmployeeListState {
    userEmployeeId: string;

    // List
    pageSize: number;
    page: number;
    hasMore: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    showNotInUse: boolean;
    items: IEmployeeItem[];

    // Edit
    selectedId: string;
    selectedCanBeRemoved: boolean;
    employeeEdit: IEmployeeEdit;
    showEmployeeDialog: boolean;
    showEditView: boolean;
}

export class SettingsEmployeeList extends React.Component<ISettingsEmployeeListProp, ISettingsEmployeeListState> {
    private containerDiv: HTMLDivElement;
    private listDiv: HTMLDivElement;
    private isLoading: boolean;

    constructor(props) {
        super(props);
        this.isLoading = false;
        this.state = {
            userEmployeeId: UserParameters.employeeId(),
            pageSize: infiniteListPageSize,
            page: 1,
            hasMore: false,
            filter: "",
            sortColumn: "number",
            sortOrderIsAsc: true,
            showNotInUse: false,
            items: [],
            selectedId: null,
            selectedCanBeRemoved: false,
            employeeEdit: null,
            showEmployeeDialog: false,
            showEditView: false
        };
    }

    searchItems = (pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, showNotInUse: boolean, resetItems: boolean, refreshList: boolean, successCallback: () => void = null) => {
        const obj = this;
        const state = this.state;
        obj.isLoading = true;
        store.customStore.dispatch(StoreActions.fetchStart());
        employeeService.getEmployeeItems(!refreshList ? pageSize : (page * infiniteListPageSize), !refreshList ? page : 1, filter, sortColumn, sortOrderIsAsc, showNotInUse).then(employeeItems => {
            let items: IEmployeeItem[];
            if (!resetItems && !refreshList) {
                const oldIds = {};
                for (let j = 0; j < state.items.length; j++) {
                    oldIds[state.items[j].id] = true;
                }
                const oldItems = state.items.slice(0);
                const newItems = employeeItems.items.filter(i => Object.prototype.isPrototypeOf.call(oldIds, i.id) ? false : (oldIds[i.id] = true));
                items = [...oldItems, ...newItems];
            } else {
                items = employeeItems.items;
            }
            obj.setState({
                page: !refreshList ? employeeItems.page : page,
                hasMore: employeeItems.hasMore,
                filter: filter,
                sortColumn: sortColumn,
                sortOrderIsAsc: sortOrderIsAsc,
                showNotInUse: showNotInUse,
                items: items
            });
            if (!Base.isNullOrUndefined(successCallback)) {
                successCallback();
            }
        },
        error => {
            handleApiError(error, store.customStore.dispatch);
        }).finally(() => {
            obj.isLoading = false;
            store.customStore.dispatch(StoreActions.fetchEnd());
        });
    };

    refreshList = () => {
        this.searchItems(this.state.pageSize, this.state.page, this.state.filter, this.state.sortColumn, this.state.sortOrderIsAsc, this.state.showNotInUse, false, true);
    };

    handleScrollSub = Base.debounce((obj: SettingsEmployeeList) => {
        if (obj.isLoading || !obj.state.hasMore) return;
        if (obj.listDiv.offsetHeight - (obj.containerDiv.clientHeight + obj.containerDiv.scrollTop) < 5) {
            obj.searchItems(obj.state.pageSize, obj.state.page + 1, obj.state.filter, obj.state.sortColumn, obj.state.sortOrderIsAsc, this.state.showNotInUse, false, false);
        }
    }, 100);

    handleScroll = (event) => {
        this.handleScrollSub(this);
    };

    componentDidMount(): void {
        this.containerDiv.addEventListener("scroll", this.handleScroll);
        this.searchItems(this.state.pageSize, this.state.page, this.state.filter, this.state.sortColumn, this.state.sortOrderIsAsc, this.state.showNotInUse, false, false);
    }

    componentWillUnmount(): void {
        this.containerDiv.removeEventListener("scroll", this.handleScroll);
    }

    changeFilter = (filter: string) => {
        this.searchItems(this.state.pageSize, 1, filter, this.state.sortColumn, this.state.sortOrderIsAsc, this.state.showNotInUse, true, false);
    };

    changeSortColumn = (sortColumn: string) => {
        const oldSortColumn = this.state.sortColumn;
        const sortOrderIsAsc = oldSortColumn === sortColumn ? !this.state.sortOrderIsAsc : true;
        this.searchItems(this.state.pageSize, 1, this.state.filter, sortColumn, sortOrderIsAsc, this.state.showNotInUse, true, false);
    };

    changeShowNotInUse = (showNotInUse: boolean) => {
        if (showNotInUse === this.state.showNotInUse) return;
        this.searchItems(this.state.pageSize, 1, this.state.filter, this.state.sortColumn, this.state.sortOrderIsAsc, showNotInUse, true, false);
    };

    getEditItem = (id: string) => {
        const obj = this;
        EmployeeOperations.getEmployeeEdit(id)
            .then(editItem => {
                obj.setState({
                    employeeEdit: editItem,
                    showEmployeeDialog: true,
                });
            });
    };

    handleAdd = () => {
        this.getEditItem(Base.emptyGuid);
    };

    handleEdit = () => {
        const selectedId = this.state.selectedId;
        if (!selectedId) return;
        this.setState({
            showEditView: true
        });
        this.getEditItem(selectedId);
    };

    handleEditDialogOk = () => {
        this.setState({
            showEmployeeDialog: false,
            showEditView: false
        });
        this.refreshList();
    };

    handleEditDialogCancel = (hasBeenChanged: boolean) => {
        this.setState({
            showEmployeeDialog: false,
            showEditView: false
        });
        if (!hasBeenChanged) return;
        this.refreshList();
    };

    handleRemove = () => {
        const obj = this;
        const selectedId = this.state.selectedId;
        if (!selectedId || this.state.userEmployeeId === selectedId) return;
        const item = this.state.items.find(i => i.id === selectedId);
        if (!item) return;
        const userParameters = new UserParameters();
        if (!userParameters.getIsOwnerAdmin() && item.getIsOwnerAdmin()) return;
        store.customStore.dispatch(StoreActions.setConfirmation(ConfirmationDialogType.Warning, t("common.warning"), t("employee.removeConfirmation", { p0: item.number }), () => {
            store.customStore.dispatch(StoreActions.clearConfirmation());
            // Call server
            store.customStore.dispatch(StoreActions.fetchStart());
            employeeService.removeEmployee(selectedId)
                .then(success => {
                    store.customStore.dispatch(StoreActions.showSuccessMessage(success.message));
                    obj.refreshList(); // TODO UPDATE ONLY CHANGED CUSTOMER DATA
                })
                .catch(error => {
                    store.customStore.dispatch(StoreActions.showErrorMessage(baseService.getErrorMessageFromError(error)));
                    return null;
                })
                .finally(() => store.customStore.dispatch(StoreActions.fetchEnd()));
        }, () => {
            store.customStore.dispatch(StoreActions.clearConfirmation());
        }));
    };

    handleClick = (id: string) => {
        const state = this.state;
        const item = state.items.find(i => i.id === id);
        const cannotBeRemoved = !UserParameters.isOwnerAdmin() && item && item.getIsOwnerAdmin() || state.userEmployeeId === id;
        this.setState({
            selectedId: id,
            selectedCanBeRemoved: !cannotBeRemoved
        });
    };

    handleDoubleClick = (id: string) => {
        this.getEditItem(id);
    };

    render() {
        const items = this.state.items;
        return (
            <div className="backgroundContent">
                <div className="row commandRow main">
                    <ToolTitle
                        title={t("employee.employees")}
                    />
                    <div className="col">
                        <ToolButton
                            title={t("common.add")}
                            enabled={true}
                            classes={"round left add"}
                            onClick={this.handleAdd}
                        />
                        <ToolButton
                            title={t("common.edit")}
                            enabled={!!this.state.selectedId}
                            classes={"round left edit"}
                            onClick={this.handleEdit}
                        />
                        <ToolButton
                            title={t("common.remove")}
                            enabled={!!this.state.selectedId && this.state.selectedCanBeRemoved}
                            classes={"round left remove"}
                            onClick={this.handleRemove}
                        />
                    </div>
                    <div className="col-auto right">
                        <CheckBox
                            classes="tool"
                            title={t("common.showNotInUse")}
                            enabled={true}
                            checked={this.state.showNotInUse}
                            onCheckboxClickBoolean={(checked: boolean) => { this.changeShowNotInUse(checked); }}
                        />
                    </div>
                    <div className="col-auto right">
                        <ListSearchFilter
                            searchFilter={this.state.filter}
                            onSearchClick={this.changeFilter}
                        />
                    </div>
                </div>
                <div>
                    <SettingsEmployeeListLineHeader
                        sortColumn={this.state.sortColumn}
                        sortOrderIsAsc={this.state.sortOrderIsAsc}
                        onColumnClick={this.changeSortColumn}
                    />
                </div>
                <div className="listContainer main employeesContainer" ref={(elem) => { this.containerDiv = elem; }}>
                    <div className="list employees striped">
                        <div className="lineContainer" ref={(elem) => { this.listDiv = elem; }}>
                            {items.map((item) =>
                                <SettingsEmployeeListLine
                                    key={item.id}
                                    item={item}
                                    selectedId={this.state.selectedId}
                                    onClick={this.handleClick}
                                    onDoubleClick={this.handleDoubleClick}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {this.state.showEmployeeDialog &&
                    <SettingsEmployeeDialog
                        showEditView={this.state.showEditView}
                        employeeEdit={this.state.employeeEdit}
                        onOk={this.handleEditDialogOk}
                        onCancel={this.handleEditDialogCancel}
                        setEmployeeParameters={this.props.setEmployeeParameters}
                    />
                }
            </div>
        );
    }
}
